<template>
  <div
    class="search-result-item"
    :class="{ 'is-focused': isFocused }"
    @click="handleClick"
    @mouseenter="$emit('focus', index)"
    data-testid="search-result-item"
    :data-result-type="result.type"
    :data-result-name="result.name"
  >
    <div class="result-content">
      <div class="result-main">
        <div class="result-header">
          <span
            class="result-name"
            v-html="highlightMatch(result.name, searchQuery)"
          ></span>
          <span class="result-type-badge tag is-small" :class="typeBadgeClass">
            {{ typeBadgeLabel }}
          </span>
        </div>

        <div
          class="result-subtitle"
          v-html="highlightMatch(result.subtitle, searchQuery)"
        ></div>

        <!-- Beer-specific metadata -->
        <div v-if="result.type === 'beer' && result.meta" class="result-meta">
          <div class="meta-items">
            <span v-if="result.meta.abv" class="meta-item">
              {{ result.meta.abv }}% ABV
            </span>
            <span v-if="result.meta.original_gravity" class="meta-item">
              {{ result.meta.original_gravity }}° OG
            </span>
            <span v-if="result.meta.average_rating" class="meta-item">
              <span class="rating-score">{{
                result.meta.average_rating.toFixed(1)
              }}</span
              >/7
              <span class="rating-count"
                >({{ result.meta.rating_count }} rating{{
                  result.meta.rating_count === 1 ? '' : 's'
                }})</span
              >
            </span>
          </div>
        </div>
      </div>

      <!-- Quick actions -->
      <div class="result-actions">
        <button
          v-if="result.type === 'beer'"
          class="button is-small is-success is-light"
          @click.stop="useInNewRating"
          title="Use in new rating"
        >
          <span class="icon is-small">
            <i class="fas fa-plus"></i>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

interface SearchResult {
  type: string
  id: string
  name: string
  subtitle: string
  url: string
  meta?: {
    brewery_guid?: string
    brewery_name?: string
    abv?: number
    original_gravity?: number
    average_rating?: number
    rating_count?: number
    relevance_score?: number
  }
}

interface Props {
  result: SearchResult
  searchQuery: string
  isFocused: boolean
  index: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  focus: [index: number]
  select: [result: SearchResult]
}>()

const router = useRouter()

const typeBadgeClass = computed(() => {
  switch (props.result.type) {
    case 'beer':
      return 'is-primary'
    case 'brewery':
      return 'is-info'
    case 'pub':
      return 'is-warning'
    case 'event':
      return 'is-success'
    case 'bjcp_style':
      return 'is-info is-light'
    case 'custom_style':
      return 'is-warning is-light'
    default:
      return 'is-light'
  }
})

const typeBadgeLabel = computed(() => {
  switch (props.result.type) {
    case 'bjcp_style':
      return 'BJCP Style'
    case 'custom_style':
      return 'Custom Style'
    default:
      return props.result.type
  }
})

const handleClick = () => {
  emit('select', props.result)
}

const useInNewRating = () => {
  if (props.result.type === 'beer' && props.result.meta) {
    router.push({
      name: 'new-rating',
      query: {
        beer_guid: props.result.id,
        beer_name: props.result.name,
        brewery_guid: props.result.meta.brewery_guid,
        brewery_name: props.result.meta.brewery_name,
        original_gravity: props.result.meta.original_gravity,
        abv: props.result.meta.abv,
      },
    })
  }
}

const highlightMatch = (text: string, query: string): string => {
  if (!query || !text) return text

  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi')
  return text.replace(regex, '<mark>$1</mark>')
}

const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
</script>

<style scoped>
.search-result-item {
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
}

.search-result-item:hover,
.search-result-item.is-focused {
  background-color: #f8f9fa;
}

.search-result-item:last-child {
  border-bottom: none;
}

.result-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

.result-main {
  flex: 1;
  min-width: 0; /* Allow text to truncate */
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.result-name {
  font-weight: 600;
  color: #363636;
  font-size: 0.95rem;
}

.result-type-badge {
  text-transform: capitalize;
  font-size: 0.7rem;
  border-radius: 10px;
}

.result-subtitle {
  color: #757575;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.result-meta {
  margin-top: 0.5rem;
}

.meta-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: #888;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.rating-score {
  font-weight: 600;
  color: #48c774;
}

.rating-count {
  color: #999;
}

.result-actions {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Highlight styles */
.result-name mark,
.result-subtitle mark {
  background-color: #fffbdd;
  color: #000;
  padding: 0;
  font-weight: 600;
}
</style>
